<script>
const DEFAULT_EXPIRY_MS = 5 * 60 * 1000

function safeHttpsUrl (value) {
  try {
    const url = new URL(String(value || '').trim())
    return url.protocol === 'https:' ? url.href : ''
  } catch (error) {
    return ''
  }
}

export default {
  name: 'KissPayDepositPanel',
  props: {
    transaction: { type: Object, required: true },
    amount: { type: Number, required: true },
    checking: { type: Boolean, default: false }
  },
  data () {
    return {
      nowMs: Date.now(),
      fallbackExpiresAtMs: Date.now() + DEFAULT_EXPIRY_MS,
      countdownTimer: null
    }
  },
  computed: {
    qrCodeUrl () {
      return safeHttpsUrl(this.transaction.qr_code_url || this.transaction.qrCodeUrl)
    },
    fallbackPaymentUrl () {
      return safeHttpsUrl(this.transaction.redirect_url || this.transaction.showurl)
    },
    displayAmount () {
      const amount = Number(this.transaction.amount_payable || this.transaction.actual_amount || 0)
      return Number.isFinite(amount) && amount > 0 ? amount : null
    },
    creditSiteName () {
      const hostname = typeof window === 'undefined' ? '' : String(window.location.hostname || '').toLowerCase()
      if (/fun-?beyond/.test(hostname)) return 'funbeyond'
      if (hostname.includes('vr1game')) return 'vr1game'
      return 'connextcc'
    },
    creditOnlyText () {
      return `ใช้เติมเครดิต ${this.creditSiteName} เท่านั้น`
    },
    expiresAtMs () {
      const createdAt = Date.parse(String(this.transaction.provider_created_at || ''))
      return Number.isFinite(createdAt) ? createdAt + DEFAULT_EXPIRY_MS : this.fallbackExpiresAtMs
    },
    remainingSeconds () {
      return Math.max(0, Math.ceil((this.expiresAtMs - this.nowMs) / 1000))
    },
    remainingText () {
      const minutes = Math.floor(this.remainingSeconds / 60)
      const seconds = this.remainingSeconds % 60
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    },
    customerScanned () {
      return this.transaction.customer_scanned === true || String(this.transaction.provider_status || '').toUpperCase() === 'S'
    }
  },
  mounted () {
    this.countdownTimer = window.setInterval(() => {
      this.nowMs = Date.now()
    }, 1000)
  },
  beforeDestroy () {
    if (this.countdownTimer) window.clearInterval(this.countdownTimer)
  }
}
</script>

<template>
  <section class="kisspay-panel" aria-live="polite">
    <header class="kisspay-panel__header">
      <div>
        <strong>KissPay</strong>
        <span>สแกน QR เพื่อชำระเงิน</span>
      </div>
      <span class="kisspay-panel__status">
        <i v-if="checking" class="fas fa-spinner fa-spin" aria-hidden="true" />
        {{ checking ? 'กำลังตรวจสอบ' : (customerScanned ? 'รอตรวจยอด' : 'รอชำระเงิน') }}
      </span>
    </header>

    <div class="kisspay-panel__body">
      <div class="kisspay-panel__countdown" role="timer" aria-atomic="true">
        <span>กรุณาชำระรายการภายใน</span>
        <strong>{{ remainingText }}</strong>
      </div>

      <div v-if="qrCodeUrl" class="kisspay-panel__qr-wrap">
        <div class="kisspay-panel__qr-frame">
          <img class="kisspay-panel__qr" :src="qrCodeUrl" alt="KissPay PromptPay QR Code">
          <div class="kisspay-panel__qr-watermark" role="note">
            {{ creditOnlyText }}
          </div>
        </div>
      </div>

      <div v-else class="kisspay-panel__fallback">
        <div>กำลังเตรียม QR Code หากยังไม่แสดงสามารถเปิดหน้าชำระเงินสำรองได้</div>
        <a v-if="fallbackPaymentUrl" :href="fallbackPaymentUrl" target="_blank" rel="noopener noreferrer">
          เปิดหน้าชำระเงิน KissPay
        </a>
      </div>

      <div v-if="displayAmount !== null" class="kisspay-panel__amount">
        <small>ยอดที่ต้องชำระ</small>
        <strong>{{ displayAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} บาท</strong>
      </div>

      <div class="kisspay-panel__notice">
        สแกน QR ด้วยบัญชีธนาคารที่ลงทะเบียนไว้ และชำระตามยอดที่แสดงเท่านั้น
      </div>
    </div>

    <footer class="kisspay-panel__actions">
      <button type="button" class="kisspay-panel__cancel" :disabled="checking" @click="$emit('cancel')">
        ยกเลิกรายการเดิมและสร้างรายการใหม่
      </button>
      <button type="button" class="kisspay-panel__close" data-dismiss="modal">
        ปิดหน้าต่าง
      </button>
    </footer>
  </section>
</template>

<style scoped>
.kisspay-panel { display: flex; flex-direction: column; width: calc(100% - 30px); max-height: calc(100vh - 70px); margin: 16px 15px 22px; overflow: hidden; border-radius: 16px; background: #0f172a; color: #fff; font-family: 'Kanit', sans-serif; box-shadow: 0 12px 30px rgba(0, 0, 0, 0.28); }
.kisspay-panel__header { display: flex; flex: 0 0 auto; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 48px 14px 16px; background: #111827; }
.kisspay-panel__header > div { display: flex; flex-direction: column; }
.kisspay-panel__header strong { color: #86efac; font-size: 18px; }
.kisspay-panel__header span { color: #cbd5e1; font-size: 12px; }
.kisspay-panel__status { white-space: nowrap; }
.kisspay-panel__status i { margin-right: 5px; color: #60a5fa; }
.kisspay-panel__body { display: flex; flex: 1 1 auto; flex-direction: column; align-items: center; justify-content: center; gap: 12px; overflow-y: auto; padding: 16px; background: #f8fafc; color: #0f172a; }
.kisspay-panel__countdown { display: flex; align-items: center; justify-content: center; gap: 8px; width: min(100%, 520px); padding: 9px 14px; border: 1px solid #fecaca; border-radius: 10px; background: #fff; color: #475569; font-size: 14px; }
.kisspay-panel__countdown strong { color: #dc2626; font-size: 22px; font-variant-numeric: tabular-nums; line-height: 1; }
.kisspay-panel__qr-wrap { display: flex; flex-direction: column; align-items: center; margin: 0 auto; }
.kisspay-panel__qr-frame { position: relative; isolation: isolate; width: min(250px, 68vw); }
.kisspay-panel__qr { display: block; width: 100%; height: auto; padding: 8px; border: 1px solid #cbd5e1; border-radius: 12px; background: #fff; }
.kisspay-panel__qr-watermark { position: absolute; top: 50%; left: 50%; z-index: 2; padding: 4px 5px; transform: translate(-50%, -50%) rotate(-3deg); color: #dc2626; font-size: clamp(11px, 2.2vw, 15px); font-weight: 700; line-height: 1.2; text-align: center; text-shadow: -1px -1px 0 rgba(255, 255, 255, 0.98), 1px -1px 0 rgba(255, 255, 255, 0.98), -1px 1px 0 rgba(255, 255, 255, 0.98), 1px 1px 0 rgba(255, 255, 255, 0.98), 0 0 7px #fff; white-space: nowrap; pointer-events: none; user-select: none; }
.kisspay-panel__amount { display: flex; flex-direction: column; align-items: center; }
.kisspay-panel__amount small { color: #64748b; }
.kisspay-panel__amount strong { color: #0f766e; font-size: 28px; }
.kisspay-panel__notice { max-width: 520px; color: #b91c1c; font-size: 13px; text-align: center; }
.kisspay-panel__fallback { max-width: 520px; padding: 18px; border: 1px solid #fde68a; border-radius: 10px; background: #fffbeb; color: #92400e; text-align: center; }
.kisspay-panel__fallback a { display: inline-block; margin-top: 10px; padding: 8px 12px; border-radius: 8px; background: #0f766e; color: #fff; }
.kisspay-panel__actions { display: grid; flex: 0 0 auto; gap: 7px; padding: 10px 12px; background: #111827; }
.kisspay-panel__cancel,
.kisspay-panel__close { width: 100%; padding: 10px 12px; border: 0; border-radius: 9px; font-size: 13px; font-weight: 600; text-align: center; }
.kisspay-panel__cancel { background: #fee2e2; color: #b91c1c; }
.kisspay-panel__close { background: #e2e8f0; color: #334155; }
.kisspay-panel__cancel:disabled,
.kisspay-panel__close:disabled { cursor: wait; opacity: 0.65; }
@media (max-width: 991.98px) {
  .kisspay-panel { width: 100vw; height: 100vh; height: 100dvh; max-height: none; margin: 0; border-radius: 0; }
  .kisspay-panel__header { padding: max(9px, env(safe-area-inset-top)) 42px 9px 14px; }
  .kisspay-panel__header strong { font-size: 16px; }
  .kisspay-panel__header span { font-size: 11px; }
  .kisspay-panel__body { gap: 9px; padding: 10px 14px; }
  .kisspay-panel__qr-frame { width: min(230px, 62vw); }
  .kisspay-panel__qr { padding: 6px; border-radius: 9px; }
  .kisspay-panel__amount strong { font-size: 25px; }
  .kisspay-panel__actions { gap: 6px; padding: 8px 10px max(8px, env(safe-area-inset-bottom)); }
  .kisspay-panel__cancel,
  .kisspay-panel__close { padding: 8px 10px; }
}
</style>
